import dayjs from 'dayjs';
import {ICheckinCycleDetail} from '@pages/dashboard/company-legacy/company-components/company-checkins/company-checkins.interface';
import {TeamFeedAnswer} from '../my_checkins/team-feed.utils';
import {markdownToPlainText} from '@ui/molecules/editor/rich-text.utils';
import {
  CycleOccurrence,
  CheckinSubmissionKind,
  ManagedSubmission,
  ManagedUser,
  checkinSubmissionStatusLabel,
  getCheckinSubmissionStatus,
  getManagedSubmissionUserId,
} from './team-management.utils';

export type ParticipantSendStatus = CheckinSubmissionKind;

export type ParticipantSend = {
  key: string;
  label: string;
  occurrence: CycleOccurrence;
  submission?: ManagedSubmission;
  status: ParticipantSendStatus;
  daysOverdue: number;
};

export const getParticipant = (
  submissions: ManagedSubmission[],
  participantId?: string,
): ManagedUser | undefined =>
  submissions.reduce<ManagedUser | undefined>((match, submission) => {
    if (match || getManagedSubmissionUserId(submission) !== participantId)
      return match;
    return typeof submission.user === 'object' ? submission.user : undefined;
  }, undefined);

export const buildParticipantIds = (occurrences: CycleOccurrence[]) => {
  const ids: string[] = [];
  occurrences.forEach((occurrence) => {
    occurrence.submissions.forEach((submission) => {
      const userId = getManagedSubmissionUserId(submission);
      if (userId && !ids.includes(userId)) ids.push(userId);
    });
  });
  return ids;
};

export const buildParticipantTimeline = (
  occurrences: CycleOccurrence[],
  participantId?: string,
): ParticipantSend[] =>
  occurrences.reduce<ParticipantSend[]>((timeline, occurrence) => {
    const submission = occurrence.submissions.find(
      (row) => getManagedSubmissionUserId(row) === participantId,
    );
    // The list endpoint creates a row for every intended recipient. If no row
    // exists, the participant was not part of that send and it must not be
    // presented as a missed submission.
    if (!submission) return timeline;
    const {kind: status, daysOverdue} = getCheckinSubmissionStatus(
      submission,
      {isLatestOccurrence: occurrence.key === occurrences[0]?.key},
    );

    timeline.push({
      key: occurrence.key,
      label: occurrence.label.replace(/^Sent\s+/i, ''),
      occurrence,
      submission,
      status,
      daysOverdue,
    });
    return timeline;
  }, []);

export const getParticipantResponseSummary = (
  timeline: ParticipantSend[],
  limit = 10,
) => {
  const eligible = timeline
    .filter((send) => send.status !== 'excused')
    .slice(0, limit);
  return {
    submitted: eligible.filter((send) => send.status === 'submitted').length,
    total: eligible.length,
  };
};

export type ParticipantRange = 'latest' | 'last-3' | 'last-6' | 'custom';

export const selectParticipantSends = (
  timeline: ParticipantSend[],
  range: ParticipantRange,
  customKeys: string[] = [],
) => {
  if (range === 'latest') return timeline.slice(0, 1);
  if (range === 'last-3') return timeline.slice(0, 3);
  if (range === 'last-6') return timeline.slice(0, 6);
  const selected = new Set(customKeys);
  return timeline.filter((send) => selected.has(send.key));
};

const csvValue = (value: unknown) =>
  `"${String(value ?? '').replace(/"/g, '""')}"`;

export const buildParticipantCsv = (
  participantName: string,
  cycle: ICheckinCycleDetail,
  sends: ParticipantSend[],
  answersBySubmissionId: Record<string, TeamFeedAnswer[]>,
) => {
  const header = [
    'Participant',
    'Cycle',
    'Send date',
    'Status',
    'Submitted at',
    'Content type',
    'Question or prompt',
    'Answer',
  ];
  const rows: unknown[][] = [];

  sends.forEach((send) => {
    const submissionId = send.submission?.id || '';
    const answers = answersBySubmissionId[submissionId] || [];
    const submittedAt = send.submission?.submittedAt
      ? dayjs(send.submission.submittedAt).format('YYYY-MM-DD HH:mm')
      : '';
    const base = [
      participantName,
      cycle.cycleName,
      send.label,
      checkinSubmissionStatusLabel({
        kind: send.status,
        daysOverdue: send.daysOverdue,
      }),
      submittedAt,
    ];

    if (!answers.length) {
      rows.push([...base, '', '', '']);
      return;
    }

    answers.forEach((answer) => {
      if (answer.kind === 'pulse') {
        rows.push([
          ...base,
          'Pulse',
          answer.prompt,
          `${answer.emoji} ${answer.label}${
            answer.feedback ? ` — ${answer.feedback}` : ''
          }`,
        ]);
      } else if (answer.kind === 'checklist') {
        rows.push([
          ...base,
          'Checklist',
          answer.prompt,
          answer.items
            .map((item) => `${item.completed ? '[x]' : '[ ]'} ${item.text}`)
            .join('; '),
        ]);
      } else {
        rows.push([
          ...base,
          'Question',
          answer.prompt,
          markdownToPlainText(answer.response),
        ]);
      }
    });
  });

  return [header, ...rows].map((row) => row.map(csvValue).join(',')).join('\n');
};
